import { format, isAfter, isBefore, isMatch, isSaturday, isValid, parse } from 'date-fns';

import type { OptimizationAvailabilities } from 'watchtower-ui/pages/OptimizationPages/OptimizationTypeUtils';
import type { OptimizationCurve } from 'watchtower-ui/pages/OptimizationPages/planning/PlanContext';
import { toParentVehicleMap } from 'watchtower-ui/pages/OptimizationPages/utils/typeGuards';
import { parseDateString } from 'watchtower-ui/utils/dateUtils';
import { stringToAbsFloat } from 'watchtower-ui/utils/utilityFunctions';

export type CSVData = {
  brand: string;
  geo: string;
  geo_group: string;
  min_spend?: string | null;
  max_spend?: string | null;
  parent_vehicle: string;
  child_vehicle: string;
  shift_between_vehicle: string;
  start_date?: string | null;
  end_date?: string | null;
  spread_type?: string;
};

export const ROW_BASE_FACTOR = 1;
export const DEFAULT_SPREAD_TYPE = '2024';

export interface IRowError {
  rowIndex: number;
  item: unknown;
  errors: { errorType: 'missing' | 'invalid'; property: string }[];
}

export interface IAvailabilitiesValuesDictionary {
  // Brand, Geo-Group and Package.Parent will
  // { "bud light":"Bud Light" }
  brand: Record<string, string>;
  geo_group: Record<string, string>;
  // Geo and Package.Child will be further grouped in sub divisions
  // { "All Other marketing":{"sampling":"Sampling"} }
  geo: Record<string, Record<string, string>>;
  package: {
    parent: Record<string, string>;
    child: Record<string, Record<string, string>>;
  };
}

export interface IIndexedCSVDataRow extends CSVData {
  rowIndex: number;
}

interface IKeyValueValidationArgs {
  key?: string;
  validKeys?: string[];
  errorPropertyKey: string;
  keyIsArray?: boolean;
  caseInsensitive?: boolean;
  useCustomValidation?: boolean;
  customValidationFunction?: (key: string) => boolean;
}

type ValidationResult = {
  flag: boolean;
  errorMessage: IRowError['errors'][number] | null;
};

type ValidationResultArray = {
  flag: boolean;
  errorMessage: IRowError['errors'][number];
}[];

/** @internal */
export const getRegionToStateMap = (availabilities: OptimizationAvailabilities): Record<string, string[]> =>
  Object.keys(availabilities?.geo?.region ?? {})?.reduce(
    (acc, regionKey) => {
      const stateList =
        (availabilities?.geo?.region as Record<string, Record<'state', string[]>>)?.[regionKey]?.state ?? [];

      return {
        ...acc,
        [regionKey]: stateList,
      };
    },
    {} as Record<string, string[]>,
  );

/** @internal */
export const getRegionKeyForState = (state: string, availabilities: OptimizationAvailabilities): string | null => {
  const regionToStateMap = getRegionToStateMap(availabilities);

  const regionKey = Object.keys(regionToStateMap).find((v) => {
    const stateList = regionToStateMap[v];
    return Boolean(stateList?.includes(state));
  });

  return regionKey ?? null;
};

/** @internal */
export const generateGeoKeyList = ({
  geoKey,
  availabilities,
}: {
  geoKey: string;
  availabilities: OptimizationAvailabilities;
}): {
  geoKeyList: string[];
  regionKeyList: string[];
} => {
  const regionKeyList = new Set<string>();
  const geoKeyList = geoKey
    .split(';')
    .filter(Boolean)
    .flatMap((v) => {
      if (v.includes('region:')) {
        // Key has region
        const regionKey = v.split(':')[1];
        if (regionKey) {
          regionKeyList.add(regionKey);

          const regionStates =
            (availabilities?.geo?.region as Record<string, Record<string, string[]>>)?.[regionKey]?.state ?? [];
          return Array.isArray(regionStates) ? Array.from(regionStates) : [];
        }
        return [];
      } else {
        const regionKey = getRegionKeyForState(v, availabilities);
        if (regionKey) {
          regionKeyList.add(regionKey);
        }
        return [v];
      }
    })
    .filter(Boolean);

  return {
    geoKeyList: Array.from(new Set(geoKeyList)),
    regionKeyList: Array.from(regionKeyList),
  };
};

export const createCaseInsensitiveDictionary = (items: string[]): Record<string, string> =>
  items.reduce((acc, item) => ({ ...acc, [item.toLowerCase()]: item }), {});

/** @internal */
export const generateAvailabilitiesValuesDictionary = (
  availabilities: OptimizationAvailabilities,
): IAvailabilitiesValuesDictionary => {
  const brandValidKeys = Array.isArray(availabilities.brand.brand) ? availabilities.brand.brand : [];
  const geoGroupValidKeys = Object.keys(availabilities.geo ?? {});
  const availableParentKeys = Object.keys(availabilities.signal.parent_vehicle ?? {});

  const brandDictionary: IAvailabilitiesValuesDictionary['brand'] = createCaseInsensitiveDictionary(brandValidKeys);

  const geoGroupDictionary: IAvailabilitiesValuesDictionary['geo_group'] =
    createCaseInsensitiveDictionary(geoGroupValidKeys);

  const geoDictionary = geoGroupValidKeys?.map((geoGroupKey) => {
    const geoGroupData = availabilities?.geo?.[geoGroupKey] ?? {};

    if (Array?.isArray(geoGroupData)) {
      // Array
      const dictionary = createCaseInsensitiveDictionary(geoGroupData);
      return [geoGroupKey, dictionary];
    } else if (geoGroupData instanceof Object) {
      // Object
      const dictionary = createCaseInsensitiveDictionary(Object.keys(geoGroupData));
      return [geoGroupKey, dictionary];
    }

    return [];
  });

  const packageParentDictionary: IAvailabilitiesValuesDictionary['package']['parent'] =
    createCaseInsensitiveDictionary(availableParentKeys);

  const packageChildDictionary = availableParentKeys?.map((parentKey) => {
    if (
      availabilities?.signal?.parent_vehicle instanceof Object &&
      !Array.isArray(availabilities?.signal?.parent_vehicle)
    ) {
      const childDictionary = createCaseInsensitiveDictionary(
        availabilities?.signal?.parent_vehicle?.[parentKey]?.vehicle ?? [],
      );

      return [parentKey, childDictionary];
    }

    return [];
  });

  return {
    brand: brandDictionary,
    geo_group: geoGroupDictionary,
    geo: Object.fromEntries(geoDictionary) as IAvailabilitiesValuesDictionary['geo'],
    package: {
      parent: packageParentDictionary,
      child: Object.fromEntries(packageChildDictionary) as IAvailabilitiesValuesDictionary['package']['child'],
    },
  };
};

export const isAllOrEmpty = (value: string): boolean => value === 'All' || value === '';

export const addErrorAndSetFlag = (
  errorMessages: IRowError['errors'],
  error: IRowError['errors'][number],
): { flag: boolean } => {
  errorMessages.push(error);
  return { flag: false };
};

/** @internal */
const createValidationError = (errorType: 'invalid' | 'missing', property: string): ValidationResultArray[0] => ({
  flag: false,
  errorMessage: {
    errorType,
    property,
  },
});

export const handleValidationResult = (
  errorMessages: IRowError['errors'],
  validationResult: { flag: boolean; errorMessage: IRowError['errors'][number] | null },
): boolean => {
  if (!validationResult.flag && validationResult.errorMessage) {
    errorMessages.push(validationResult.errorMessage);
    return false;
  }
  return true;
};

/** @internal */
const createValidationResponse = (
  isValidInput: boolean,
  errorPropertyKey: string,
  errorType: 'invalid' | 'missing' = 'invalid',
): ValidationResult => ({
  flag: isValidInput,
  errorMessage: isValidInput
    ? null
    : {
        errorType,
        property: errorPropertyKey,
      },
});

/** @internal */
export const keyValueValidation = ({
  key,
  validKeys,
  errorPropertyKey,
  keyIsArray = false,
  caseInsensitive = false,
  useCustomValidation = false,
  customValidationFunction,
}: IKeyValueValidationArgs): ValidationResult => {
  if (useCustomValidation) {
    const isValidResult = !customValidationFunction || customValidationFunction(key ?? '');
    return createValidationResponse(isValidResult, errorPropertyKey);
  }
  if (keyIsArray) {
    if (key?.toLowerCase() === 'all' || key === '') {
      return createValidationResponse(true, errorPropertyKey);
    }

    const keys = key?.split(';')?.filter(Boolean) || [];
    const isValidResult = keys.every((keyValue) => {
      const finalKeyValue = caseInsensitive ? keyValue?.toLowerCase() : keyValue;
      return validKeys?.includes(finalKeyValue);
    });

    return createValidationResponse(isValidResult, errorPropertyKey);
  }

  const keyToCheck = caseInsensitive ? key?.toLowerCase() : key;
  const isValidResult = validKeys?.includes(keyToCheck ?? '') || false;
  return createValidationResponse(isValidResult, errorPropertyKey);
};

/** @internal */
export const checkIfKeyisValid = ({
  key,
  validKeys,
  errorPropertyKey,
  keyIsArray = false,
  useCustomValidation = false,
  customValidationFunction = undefined,
  caseInsensitive = false,
  emptyKeyIsValid = false,
}: IKeyValueValidationArgs & {
  emptyKeyIsValid?: boolean;
}): ValidationResult => {
  if ((emptyKeyIsValid && typeof key === 'string') || (!emptyKeyIsValid && key)) {
    return keyValueValidation({
      key,
      validKeys,
      errorPropertyKey,
      customValidationFunction,
      keyIsArray,
      useCustomValidation,
      caseInsensitive,
    });
  }
  return createValidationResponse(false, errorPropertyKey, 'missing');
};

const validateVehicleFields = (
  item: CSVData,
  availabilitiesValuesDictionary: IAvailabilitiesValuesDictionary,
): {
  flag: boolean;
  errorMessages: IRowError['errors'];
} => {
  const errorMessages: IRowError['errors'] = [];
  let flag = true;

  // First check if parent_vehicle exists
  if (!item.parent_vehicle) {
    const { flag: newFlag } = addErrorAndSetFlag(errorMessages, {
      errorType: 'missing',
      property: 'parent_vehicle',
    });
    flag = newFlag;
    return { flag, errorMessages };
  }

  // Validate parent_vehicle using the dictionary
  const { flag: parentFlag, errorMessage: parentError } = checkIfKeyisValid({
    key: item.parent_vehicle,
    validKeys: Object.keys(availabilitiesValuesDictionary.package.parent),
    errorPropertyKey: 'parent_vehicle',
    caseInsensitive: true,
  });

  if (!parentFlag && parentError) {
    const { flag: newFlag } = addErrorAndSetFlag(errorMessages, parentError);
    flag = newFlag;
    return { flag, errorMessages };
  }

  // Determine effective shift_between_vehicle value
  let effectiveShiftBetween: boolean | null = false; // default for empty value

  if (item.shift_between_vehicle) {
    if (['yes', 'no'].includes(item.shift_between_vehicle.toLowerCase())) {
      effectiveShiftBetween = item.shift_between_vehicle.toLowerCase() === 'yes';
    } else {
      effectiveShiftBetween = null; // invalid value
    }
  }

  if (effectiveShiftBetween === null) {
    // shift_between_vehicle is present but invalid
    const { flag: newFlag } = addErrorAndSetFlag(errorMessages, {
      errorType: 'invalid',
      property: 'shift_between_vehicle',
    });
    flag = newFlag;
  } else if (effectiveShiftBetween) {
    // shift_between_vehicle is "Yes"
    if (item?.child_vehicle !== '' && item?.child_vehicle?.toLowerCase() !== 'all') {
      // Only validate if not empty string and not "all"
      const parentKey = availabilitiesValuesDictionary?.package?.parent?.[item?.parent_vehicle?.toLowerCase()];

      const { flag: childFlag, errorMessage: childError } = checkIfKeyisValid({
        key: item.child_vehicle,
        validKeys: Object.keys(availabilitiesValuesDictionary?.package?.child?.[parentKey as string] || {}),
        errorPropertyKey: 'child_vehicle',
        keyIsArray: true,
        caseInsensitive: true,
        emptyKeyIsValid: true,
      });
      if (!childFlag && childError) {
        const { flag: newFlag } = addErrorAndSetFlag(errorMessages, childError);
        flag = newFlag;
      }
    }
  } else {
    // shift_between_vehicle is "No" - only empty string is valid
    const isChildVehicleNotEmpty = item?.child_vehicle !== '';
    if (isChildVehicleNotEmpty) {
      const { flag: newFlag } = addErrorAndSetFlag(errorMessages, {
        errorType: 'invalid',
        property: 'child_vehicle',
      });
      flag = newFlag;
    }
  }

  return { flag, errorMessages };
};

/** @internal */
export const validateDateForCSV = (val: string) => {
  if (isMatch(val, 'M/dd/yyyy')) {
    // Date is in good format
    const parsedISODate = parse(val, 'M/dd/yyyy', new Date());
    return isValid(parsedISODate) && isSaturday(parsedISODate);
  } else {
    // Take the val with native date
    const dateObj = new Date(val);
    return isValid(dateObj) && isSaturday(dateObj);
  }
};

/** @internal */
export const validateRecordDates = (
  item: Pick<IIndexedCSVDataRow, 'start_date' | 'end_date'>,
): ValidationResultArray => {
  const finalErrorResponses: ValidationResultArray = [];

  // Both present / Both Not Present => Valid
  // Only one present => Invalid

  const dateValueArr = [
    { propertyKey: 'start_date' as const, value: item?.start_date },
    { propertyKey: 'end_date' as const, value: item?.end_date },
  ];

  if ((item?.start_date && item?.end_date) || (!item?.start_date && !item?.end_date)) {
    // Either both dates are present or both dates are absent
    // Validate both dates

    dateValueArr
      ?.filter((v) => v?.value && !validateDateForCSV(v?.value))
      ?.forEach((v) => {
        finalErrorResponses?.push(createValidationError('invalid', v?.propertyKey));
      });
  } else {
    // Either one is present
    dateValueArr
      ?.filter((v) => !v?.value)
      ?.forEach((v) => {
        finalErrorResponses?.push(createValidationError('missing', v?.propertyKey));
      });
  }
  return finalErrorResponses;
};

/** @internal */
export const validateDataSpends = (
  item: Pick<IIndexedCSVDataRow, 'min_spend' | 'max_spend'>,
): ValidationResultArray => {
  const finalErrorResponses: ValidationResultArray = [];

  // Both present / Both Not Present => Valid
  // Only one present => Valid

  const spendValuesArr = [
    { propertyKey: 'min_spend' as const, value: item?.min_spend },
    { propertyKey: 'max_spend' as const, value: item?.max_spend },
  ];

  const truthySpendValues = spendValuesArr?.filter((v) => v?.value && typeof v?.value === 'string');

  const addInvalidError = (propertyKey: string) => {
    finalErrorResponses.push(createValidationError('invalid', propertyKey));
  };

  // Validate numeric values and add errors for invalid ones
  const validateSpendValues = (values: typeof truthySpendValues) => {
    values.filter((v) => Number.isNaN(stringToAbsFloat(v?.value))).forEach((v) => addInvalidError(v.propertyKey));
  };

  if (truthySpendValues?.length === 0 || truthySpendValues?.length === 2) {
    if (truthySpendValues?.length === 2) {
      const numericValues = truthySpendValues.filter((v) => !Number.isNaN(stringToAbsFloat(v?.value)));

      if (numericValues.length === 2) {
        const min = stringToAbsFloat(numericValues[0]?.value ?? '0');
        const max = stringToAbsFloat(numericValues[1]?.value ?? '0');
        if (max < min) {
          addInvalidError('max_spend');
        }
      } else {
        validateSpendValues(truthySpendValues);
      }
    }
  } else if (truthySpendValues?.length === 1) {
    validateSpendValues(truthySpendValues);
  }

  return finalErrorResponses;
};

/** @internal */
export const validateStateGeoValue = (
  key: string,
  item: IIndexedCSVDataRow,
  availabilitiesValuesDictionary: IAvailabilitiesValuesDictionary,
): boolean => {
  const regionValidKeys = Object.keys(availabilitiesValuesDictionary?.geo?.region ?? {});
  const geoGroupKey = item?.geo_group?.toLowerCase();
  const caseValidGeoGroupKey = availabilitiesValuesDictionary?.geo_group?.[geoGroupKey];
  const geoValidKeys = Object.keys(availabilitiesValuesDictionary?.geo?.[caseValidGeoGroupKey ?? ''] ?? {});

  if (!key || key.toLowerCase() === 'all') return true;

  const keys = key.split(';').filter(Boolean);

  return keys.every((keyValue) => {
    const finalKeyValue = keyValue?.toLowerCase();

    if (finalKeyValue.startsWith('region:')) {
      const [, regionValue] = keyValue.split(':').map((part) => part.trim());
      return regionValue && regionValidKeys?.includes(regionValue.toLowerCase());
    }

    return geoValidKeys.includes(finalKeyValue);
  });
};

// Create And Get
/** @internal */
export const createGeoSpreadPayload = ({
  geoSpreadKey,
  item,
  availabilities,
}: {
  geoSpreadKey: string;
  item: IIndexedCSVDataRow;
  availabilities: OptimizationAvailabilities;
}) => {
  const geoSpreadPayload: [string, unknown][] = [];

  let geoSpecificPayload: string[] = [];
  let regionSpecificPayload: string[] = [];

  if (typeof item?.geo === 'string') {
    if (isAllOrEmpty(item.geo)) {
      const geoGroupData = availabilities.geo?.[item.geo_group];
      geoSpecificPayload = Array.isArray(geoGroupData) ? geoGroupData : Object.keys(geoGroupData ?? {});

      if (['state', 'state_pop'].includes(geoSpreadKey)) {
        regionSpecificPayload = Object.keys(availabilities.geo?.region ?? {});
      }
    } else {
      const { geoKeyList, regionKeyList } = generateGeoKeyList({
        geoKey: item.geo,
        availabilities,
      });

      if (geoKeyList?.length > 0) {
        geoSpecificPayload = Array.from(geoKeyList);
      }
      regionSpecificPayload = Array.from(regionKeyList);
    }
  }

  geoSpreadPayload.push([geoSpreadKey, geoSpecificPayload]);
  if (regionSpecificPayload.length > 0) {
    geoSpreadPayload.push(['region', regionSpecificPayload]);
  }

  return geoSpreadPayload;
};

/** @internal */
export const createSpendPayload = ({ minSpend, maxSpend }: { minSpend: string | null; maxSpend: string | null }) => {
  const spendPayload: ['minSpend' | 'maxSpend', number][] = [];

  const spendValues = [
    { key: 'minSpend' as const, value: minSpend },
    { key: 'maxSpend' as const, value: maxSpend },
  ];

  spendValues.forEach(({ key, value }) => {
    // Only process values that are not null, undefined, or empty strings
    if (value && value.trim() !== '') {
      const numericValue = stringToAbsFloat(value);
      if (!Number.isNaN(numericValue)) {
        spendPayload.push([key, numericValue]);
      }
    }
  });

  return spendPayload;
};

/** @internal */
export const createBrandsPayload = ({
  item,
  availabilities,
}: {
  item: CSVData & {
    rowIndex: number;
  };
  availabilities: OptimizationAvailabilities;
}) => {
  const brandsPayload: ['brands', string[]][] = [['brands', []]];

  if (typeof item?.brand === 'string') {
    if (isAllOrEmpty(item.brand)) {
      const brandAllKeys = Array.isArray(availabilities.brand?.brand) ? availabilities.brand.brand : [];
      if (brandAllKeys?.length > 0) {
        brandsPayload.push(['brands', brandAllKeys]);
      }
    } else {
      const brandKeyList = item.brand.split(';').filter(Boolean);
      if (brandKeyList?.length > 0) {
        brandsPayload.push(['brands', brandKeyList]);
      }
    }
  }

  return brandsPayload;
};

const getUploadedDateRange = (
  item: Pick<IIndexedCSVDataRow, 'start_date' | 'end_date'>,
): [string, string] | null => {
  if (!item.start_date || !item.end_date) {
    return null;
  }

  const parsedStartDate = parseDateString(item.start_date);
  const parsedEndDate = parseDateString(item.end_date);

  if (!parsedStartDate || !parsedEndDate) {
    return null;
  }

  return [format(parsedStartDate, 'yyyy-MM-dd'), format(parsedEndDate, 'yyyy-MM-dd')];
};

const createCommonPayloads = (item: IIndexedCSVDataRow, availabilities: OptimizationAvailabilities) => {
  const geoSpreadKey = item?.geo_group || '';

  const geoSpreadPayload = geoSpreadKey
    ? createGeoSpreadPayload({
        geoSpreadKey,
        item,
        availabilities,
      })
    : [];

  let dateRangePayload: [string, string] | null = null;
  if (
    item?.start_date &&
    parseDateString(item?.start_date) !== null &&
    item?.end_date &&
    parseDateString(item?.end_date) !== null
  ) {
    dateRangePayload = [
      format(parseDateString(item?.start_date) as Date, 'yyyy-MM-dd'),
      format(parseDateString(item?.end_date) as Date, 'yyyy-MM-dd'),
    ];
  }

  const brandsPayload = createBrandsPayload({
    item,
    availabilities,
  });

  const spendPayload = createSpendPayload({
    maxSpend: item?.max_spend ?? '',
    minSpend: item?.min_spend ?? '',
  });

  return {
    geoSpreadPayload,
    dateRangePayload,
    brandsPayload,
    spendPayload,
  };
};

export const getAllParentChildItems = (parentKey: string, availabilities: OptimizationAvailabilities): string[] => {
  const parentVehicleData = toParentVehicleMap(availabilities?.signal?.parent_vehicle);
  return parentVehicleData[parentKey]?.vehicle ?? [];
};

const getDisbaledChildVehicleItems = (
  parentKey: string,
  activeChildVehicleList: string[],
  availabilities: OptimizationAvailabilities,
  defaultValues: Partial<OptimizationCurve> & {
    geoSpread: string;
  },
  enableAllItems: boolean = false,
): OptimizationCurve[] => {
  const parentAllChildVehicles = getAllParentChildItems(parentKey, availabilities);

  const disabledChildItems = parentAllChildVehicles?.filter((key) => !activeChildVehicleList?.includes(key));

  const disabledPayload: ['disabled', boolean | undefined][] = [];

  if (!enableAllItems) {
    disabledPayload.push(['disabled', true]);
  }

  return disabledChildItems?.map((key) => ({
    ...defaultValues,
    brands: null,
    vehicleName: key,
    vehicleSpread: 'vehicle',
    geoNames: null,
    dateRange: defaultValues.dateRange ?? null,
    spread_type: defaultValues.spread_type ?? DEFAULT_SPREAD_TYPE,
    ...Object.fromEntries(disabledPayload),
  }));
};

/** @internal */
const processArrayKeys = (key: string, processor: (keyValue: string) => string): string => {
  if (key?.toLowerCase() === 'all') {
    return 'All';
  }
  if (key === '') {
    return key;
  }
  return key?.split(';')?.filter(Boolean)?.map(processor)?.filter(Boolean)?.join(';');
};

/** @internal */
export const getValueFromDictionary = ({
  key,
  dictionary,
  keyIsArray = false,
  useCustomReplaceFunction = false,
  customReplaceFunction,
}: {
  key: string;
  dictionary: Record<string, string>;
  keyIsArray?: boolean;
  useCustomReplaceFunction?: boolean;
  customReplaceFunction?: (val: string) => string;
}): string => {
  if (useCustomReplaceFunction) {
    return customReplaceFunction ? customReplaceFunction(key) : key;
  }
  if (keyIsArray) {
    return processArrayKeys(key, (v) => dictionary?.[v?.toLowerCase()] ?? v);
  }
  return dictionary?.[key?.toLowerCase()] ?? key;
};

// Find And Replace
/** @internal */
export const findMaxAndMinFromDateStringList = (
  dateList: string[],
): {
  max: string | null;
  min: string | null;
} => {
  if (Array?.isArray(dateList) && dateList?.length > 0) {
    const res: {
      max: string | null;
      min: string | null;
    } = dateList?.reduce(
      (acc, item) => {
        const maxDate = parseDateString(acc?.max);
        const minDate = parseDateString(acc?.min);
        const currItemDate = parseDateString(item);

        // For min
        if (minDate) {
          if (currItemDate) {
            if (isBefore(currItemDate, minDate)) {
              acc.min = item;
            }
          }
        } else {
          // Techinically will never execute
          acc.min = item;
        }

        // For max
        if (maxDate) {
          if (currItemDate) {
            if (isAfter(currItemDate, maxDate)) {
              acc.max = item;
            }
          }
        } else {
          // Technically will never execute
          acc.max = item;
        }

        return acc;
      },
      {
        max: dateList?.at(0) ?? '',
        min: dateList?.at(0) ?? '',
      },
    );

    // Ensure both max and min are valid dates
    if (!parseDateString(res.max ?? '')) res.max = null;
    if (!parseDateString(res.min ?? '')) res.min = null;

    return res;
  }
  return {
    max: null,
    min: null,
  };
};

/** @internal */
export const replaceStateGeoValue = (
  key: string,
  geoDictionary: Record<string, string>,
  regionDictionary: Record<string, string>,
): string =>
  processArrayKeys(key, (v) => {
    const keyValue = v?.toLowerCase();
    if (keyValue?.includes('region:')) {
      const regionKey = keyValue?.split(':')?.[1]?.trim()?.toLowerCase();
      return regionKey && regionDictionary?.[regionKey] ? `region:${regionDictionary[regionKey]}` : '';
    }
    return geoDictionary?.[v?.toLowerCase()] ?? v;
  });

/** @internal */
export const replaceValuesWithValidCaseCounterpart = ({
  validatedParsedData,
  availabilitiesValuesDictionary,
}: {
  validatedParsedData: IIndexedCSVDataRow[];
  availabilitiesValuesDictionary: IAvailabilitiesValuesDictionary;
}): IIndexedCSVDataRow[] => {
  const filteredParsedData = validatedParsedData?.map((item) => {
    const geoGroupValue = getValueFromDictionary({
      key: item?.geo_group,
      dictionary: availabilitiesValuesDictionary?.geo_group,
    });

    const geoCombinedDictionary = {
      ...availabilitiesValuesDictionary?.geo?.[geoGroupValue],
    };

    // Get parent vehicle with correct case
    const parentVehicleValue = getValueFromDictionary({
      key: item?.parent_vehicle,
      dictionary: availabilitiesValuesDictionary?.package?.parent,
    });

    // Convert shift_between_vehicle to correct case
    const shiftBetweenVehicleValue = item?.shift_between_vehicle?.toLowerCase() === 'yes' ? 'Yes' : 'No';
    // Handle child_vehicle case conversion
    const childVehicleValue =
      shiftBetweenVehicleValue === 'Yes'
        ? getValueFromDictionary({
            key: item?.child_vehicle,
            dictionary: availabilitiesValuesDictionary?.package?.child?.[parentVehicleValue] ?? {},
            keyIsArray: true,
          })
        : '';

    return {
      ...item,
      brand: getValueFromDictionary({
        key: item?.brand,
        dictionary: availabilitiesValuesDictionary?.brand,
        keyIsArray: true,
      }),
      geo: getValueFromDictionary({
        key: item?.geo,
        dictionary: geoCombinedDictionary,
        keyIsArray: true,
        useCustomReplaceFunction: Boolean(
          item?.geo_group?.toLowerCase() === 'state' || item?.geo_group?.toLowerCase() === 'state_pop',
        ),
        customReplaceFunction: (val: string) =>
          replaceStateGeoValue(val, geoCombinedDictionary, availabilitiesValuesDictionary?.geo?.region ?? {}),
      }),
      geo_group: geoGroupValue,
      parent_vehicle: parentVehicleValue,
      child_vehicle: childVehicleValue,
      shift_between_vehicle: shiftBetweenVehicleValue,
    };
  });

  return filteredParsedData;
};

// Filter And Map
/** @internal */
export const filterOutEmptyRow = (item: CSVData): boolean =>
  Boolean(
    item?.geo_group &&
      item?.parent_vehicle &&
      typeof item?.geo_group === 'string' &&
      typeof item?.parent_vehicle === 'string',
  );

const plCSVDataFilterErrorRows = ({
  item,
  availabilitiesValuesDictionary,
}: {
  item: IIndexedCSVDataRow;
  availabilitiesValuesDictionary: IAvailabilitiesValuesDictionary;
}): {
  flag: boolean;
  errorMessages: IRowError['errors'];
} => {
  let flag = true;
  let errorMessages: IRowError['errors'] = [];

  // Validate vehicle fields
  const { flag: vehicleFlag, errorMessages: vehicleErrors } = validateVehicleFields(
    item,
    availabilitiesValuesDictionary,
  );
  if (!vehicleFlag) {
    errorMessages = [...errorMessages, ...vehicleErrors];
    flag = false;
  }

  // Validate brand
  const { flag: brandFlag, errorMessage: brandErrorMessage } = checkIfKeyisValid({
    key: item?.brand,
    validKeys: Object.keys(availabilitiesValuesDictionary?.brand),
    errorPropertyKey: 'brand',
    keyIsArray: true,
    emptyKeyIsValid: true,
    caseInsensitive: true,
  });

  if (!handleValidationResult(errorMessages, { flag: brandFlag, errorMessage: brandErrorMessage })) {
    flag = false;
  }

  // Validate geo_group
  const { flag: geoGroupFlag, errorMessage: geoGroupErrorMessage } = checkIfKeyisValid({
    key: item?.geo_group,
    validKeys: Object.keys(availabilitiesValuesDictionary?.geo_group),
    errorPropertyKey: 'geo_group',
    caseInsensitive: true,
  });

  if (!handleValidationResult(errorMessages, { flag: geoGroupFlag, errorMessage: geoGroupErrorMessage })) {
    flag = false;
  } else {
    // Validate geo only if geo_group is valid
    const geoGroupKey = item?.geo_group?.toLowerCase();
    const caseValidGeoGroupKey = availabilitiesValuesDictionary?.geo_group?.[geoGroupKey];
    const geoValidKeys = Object.keys(availabilitiesValuesDictionary?.geo?.[caseValidGeoGroupKey ?? ''] ?? {});

    const { flag: geoFlag, errorMessage: geoErrorMessage } = checkIfKeyisValid({
      key: item?.geo,
      validKeys: geoValidKeys,
      errorPropertyKey: 'geo',
      keyIsArray: true,
      emptyKeyIsValid: true,
      caseInsensitive: true,
      useCustomValidation: Boolean(geoGroupKey === 'state' || geoGroupKey === 'state_pop'),
      customValidationFunction: (val: string) => validateStateGeoValue(val, item, availabilitiesValuesDictionary),
    });

    if (!handleValidationResult(errorMessages, { flag: geoFlag, errorMessage: geoErrorMessage })) {
      flag = false;
    }
  }

  // Validate dates
  const datesErrorResponses = validateRecordDates({
    start_date: item?.start_date,
    end_date: item?.end_date,
  });
  if (datesErrorResponses && datesErrorResponses?.length > 0) {
    flag = false;
    const dateErrorMessages =
      Array?.isArray(datesErrorResponses) && datesErrorResponses?.length > 0
        ? datesErrorResponses?.map((v) => v?.errorMessage)
        : [];
    errorMessages = [...errorMessages, ...dateErrorMessages];
  }

  // Validate spends
  const spendErrorResponse = validateDataSpends({
    min_spend: item?.min_spend,
    max_spend: item?.max_spend,
  });
  if (spendErrorResponse && spendErrorResponse?.length > 0) {
    flag = false;
    const spendErrorMessages =
      Array?.isArray(spendErrorResponse) && spendErrorResponse?.length > 0
        ? spendErrorResponse?.map((v) => v?.errorMessage)
        : [];
    errorMessages = [...errorMessages, ...spendErrorMessages];
  }

  return {
    flag,
    errorMessages,
  };
};

const mapCSVDataToOptimizationCurves = ({
  csvDataItems,
  vehicleSpreadKey,
  parentVehicle,
  availabilities,
  defaultValuesIfCSVItemsAreEmtpy = {},
  includeChildren = true,
  enableAllChildren = false,
  isKeyHavingMultipleChildVehicles = false,
}: {
  csvDataItems: IIndexedCSVDataRow[];
  vehicleSpreadKey: 'parent_vehicle' | 'vehicle';
  parentVehicle: string;
  availabilities: OptimizationAvailabilities;
  defaultValuesIfCSVItemsAreEmtpy?: Partial<OptimizationCurve>;
  includeChildren?: boolean;
  enableAllChildren?: boolean;
  isKeyHavingMultipleChildVehicles?: boolean;
}): OptimizationCurve[] => {
  const childrenPayload: [string, unknown][] = [];
  if (includeChildren) {
    childrenPayload.push(['children', undefined]);
  }

  // Get list of active child vehicles & This will also depend on isKeyHavingMultipleChildVehicles
  const activeChildVehicleList = Array.from(
    new Set(
      csvDataItems.flatMap((item) => {
        if (isKeyHavingMultipleChildVehicles) {
          return item?.child_vehicle?.split(';') ?? [];
        } else {
          return item?.child_vehicle ? [item.child_vehicle] : [];
        }
      }),
    ),
  ).filter(Boolean);

  const inheritedChildDates = csvDataItems.flatMap((item) => getUploadedDateRange(item) ?? []);
  const { min: inheritedChildStartDate, max: inheritedChildEndDate } =
    findMaxAndMinFromDateStringList(inheritedChildDates);
  const inheritedChildDateRange =
    inheritedChildStartDate && inheritedChildEndDate
      ? ([inheritedChildStartDate, inheritedChildEndDate] as [string, string])
      : null;

  // Only get disabled items for child vehicles
  const parentVehicleChildDisabledItems: OptimizationCurve[] =
    vehicleSpreadKey === 'vehicle' && parentVehicle
      ? getDisbaledChildVehicleItems(
          parentVehicle,
          activeChildVehicleList,
          availabilities,
          {
            geoSpread: csvDataItems[0]?.geo_group ?? '',
            dateRange: inheritedChildDateRange,
            ...defaultValuesIfCSVItemsAreEmtpy,
          },
          enableAllChildren,
        )
      : [];

  const csvOptimizationItems: OptimizationCurve[] = csvDataItems.flatMap((item) => {
    const geoSpreadKey = item.geo_group || '';

    const geoSpreadPayload = geoSpreadKey
      ? createGeoSpreadPayload({
          geoSpreadKey,
          item,
          availabilities,
        })
      : [];

    const dateRangePayload = getUploadedDateRange(item);

    const brandsPayload = createBrandsPayload({
      item,
      availabilities,
    });

    const spendPayload = createSpendPayload({
      maxSpend: item.max_spend ?? '',
      minSpend: item.min_spend ?? '',
    });

    if (isKeyHavingMultipleChildVehicles) {
      // Handle multiple child vehicles

      const activeVehiclesInItem = item?.child_vehicle?.split(';');
      const finalItems: OptimizationCurve[] = activeVehiclesInItem?.map((vehicle) => ({
        ...(Object.fromEntries(brandsPayload) as { brands: string[] }),
        ...Object.fromEntries(geoSpreadPayload),
        geoSpread: item?.geo_group,
        geoNames: null,
        vehicleName: vehicle,
        vehicleSpread: vehicleSpreadKey,
        dateRange: dateRangePayload,
        spread_type: item?.spread_type ?? DEFAULT_SPREAD_TYPE,
      }));
      return finalItems;
    } else {
      return [
        {
          ...(Object.fromEntries(brandsPayload) as { brands: string[] }),
          ...Object.fromEntries(geoSpreadPayload),
          ...Object.fromEntries(spendPayload),
          vehicleName: vehicleSpreadKey === 'parent_vehicle' ? item.parent_vehicle : item.child_vehicle,
          vehicleSpread: vehicleSpreadKey,
          geoSpread: geoSpreadKey,
          geoNames: null,
          dateRange: dateRangePayload,
          spread_type: item?.spread_type ?? DEFAULT_SPREAD_TYPE,
          ...Object.fromEntries(childrenPayload),
        },
      ];
    }
  });

  return [...csvOptimizationItems, ...parentVehicleChildDisabledItems];
};

const aggregateValuesIntoParent = (
  parentCurve: OptimizationCurve,
  children: OptimizationCurve[],
): OptimizationCurve => {
  const curveAggregatedPayload: (
    | [string, string[] | null]
    | ['minSpend' | 'maxSpend', number | undefined]
    | ['dateRange', [string, string] | null]
  )[] = [['brands', null]];

  // Combine brands
  const childBrands = children.flatMap((child) => (Array.isArray(child?.brands) ? child.brands : []));
  const parentBrands = Array.isArray(parentCurve?.brands) ? parentCurve.brands : [];
  const allBrands = Array.from(new Set([...parentBrands, ...childBrands]));

  if (allBrands?.length > 0) {
    curveAggregatedPayload.push(['brands', allBrands]);
  }

  // Combine geo values
  const parentGeoSpread = parentCurve?.geoSpread as keyof OptimizationCurve;
  const parentGeoValues = Array.isArray(parentCurve?.[parentGeoSpread])
    ? (parentCurve[parentGeoSpread] as string[])
    : [];

  const childGeoValues = children.flatMap((child) =>
    Array.isArray(child?.[child.geoSpread as keyof OptimizationCurve])
      ? (child[child.geoSpread as keyof OptimizationCurve] as string[])
      : [],
  );

  const allGeoValues = Array.from(new Set([...parentGeoValues, ...childGeoValues]));

  if (allGeoValues?.length > 0) {
    curveAggregatedPayload.push([parentGeoSpread, allGeoValues]);
  }

  // Combine date ranges
  const allDates = [...children, parentCurve]
    .filter((item) => Array.isArray(item?.dateRange))
    .flatMap((item) => item.dateRange || []);

  if (allDates?.length > 0) {
    const { min: startDate, max: endDate } = findMaxAndMinFromDateStringList(allDates);
    if (startDate && endDate) {
      curveAggregatedPayload.push(['dateRange', [startDate, endDate]]);
    }
  }

  // Sum up spends
  if (children.some((child) => typeof child?.minSpend === 'number')) {
    const childMinSpendSum = children.reduce(
      (sum, child) => sum + (typeof child?.minSpend === 'number' ? child.minSpend : 0),
      0,
    );
    const parentMinSpend = typeof parentCurve?.minSpend === 'number' ? parentCurve.minSpend : 0;
    curveAggregatedPayload.push(['minSpend', childMinSpendSum + parentMinSpend]);
  }

  if (children.some((child) => typeof child?.maxSpend === 'number')) {
    const childMaxSpendSum = children.reduce(
      (sum, child) => sum + (typeof child?.maxSpend === 'number' ? child.maxSpend : 0),
      0,
    );
    const parentMaxSpend = typeof parentCurve?.maxSpend === 'number' ? parentCurve.maxSpend : 0;
    curveAggregatedPayload.push(['maxSpend', childMaxSpendSum + parentMaxSpend]);
  }

  return {
    ...parentCurve,
    ...(Object.fromEntries(curveAggregatedPayload) as OptimizationCurve),
  };
};

/** @internal */
export const parseAndFilterCSVData = ({
  availabilities,
  parsedData,
}: {
  availabilities: OptimizationAvailabilities;
  parsedData: CSVData[];
}): {
  filteredRows: OptimizationCurve[];
  errorRows: IRowError[];
  validRowsCount: number;
  isWarningFlagged: boolean;
} => {
  const errorRowsArray: IRowError[] = [];
  let isWarningFlagged = false;

  const availabilitiesValuesDictionary = generateAvailabilitiesValuesDictionary(availabilities);

  // Filter out error rows
  const validatedParsedData: IIndexedCSVDataRow[] = parsedData
    .map((item, index) => ({ ...item, rowIndex: index }))
    .filter((item, index) => {
      const { flag, errorMessages } = plCSVDataFilterErrorRows({
        item,
        availabilitiesValuesDictionary,
      });

      if (!flag) {
        errorRowsArray.push({
          item,
          rowIndex: index,
          errors: errorMessages,
        });
      }
      return flag;
    });

  // Replace values with correct case
  const filteredParsedData = replaceValuesWithValidCaseCounterpart({
    validatedParsedData,
    availabilitiesValuesDictionary,
  });

  // Group by parent vehicle
  const groupedByParent = Object.groupBy(filteredParsedData, (item) => item.parent_vehicle);

  const finalResponseItems = Object.entries(groupedByParent).flatMap(([parentVehicle, items]) => {
    // Get parent records (shift_between_vehicle: "No")
    const parentRecords = items?.filter((item) => item.shift_between_vehicle === 'No');
    // Get all child records (shift_between_vehicle: "Yes")
    const childRecords = items?.filter((item) => item.shift_between_vehicle === 'Yes') ?? [];

    const curves: OptimizationCurve[] = [];

    // Add separate curves for each parent record
    if (parentRecords && parentRecords?.length > 0) {
      parentRecords?.forEach((parentRecord) => {
        const geoSpreadPayload = parentRecord.geo_group
          ? createGeoSpreadPayload({
              geoSpreadKey: parentRecord.geo_group,
              item: parentRecord,
              availabilities,
            })
          : [];

        const brandsPayload = createBrandsPayload({
          item: parentRecord,
          availabilities,
        });

        const spendPayload = createSpendPayload({
          minSpend: parentRecord.min_spend ?? '',
          maxSpend: parentRecord.max_spend ?? '',
        });

        let dateRange: [string, string] | null = null;
        if (
          parentRecord.start_date &&
          parseDateString(parentRecord.start_date) !== null &&
          parentRecord.end_date &&
          parseDateString(parentRecord.end_date) !== null
        ) {
          dateRange = [
            format(parseDateString(parentRecord.start_date) as Date, 'yyyy-MM-dd'),
            format(parseDateString(parentRecord.end_date) as Date, 'yyyy-MM-dd'),
          ];
        }

        curves.push({
          brands: null,
          vehicleName: parentVehicle,
          vehicleSpread: 'parent_vehicle',
          geoSpread: parentRecord.geo_group || '',
          geoNames: null,
          minSpend: undefined,
          maxSpend: undefined,
          spread_type: parentRecord?.spread_type ?? DEFAULT_SPREAD_TYPE,
          ...Object.fromEntries(brandsPayload),
          ...Object.fromEntries(geoSpreadPayload),
          ...Object.fromEntries(spendPayload),
          dateRange,
        });
      });
    }

    // If child records exist, create a parent curve from them
    if (childRecords && childRecords?.length > 0) {
      // Filter Out Multiple Child Items
      const multipleChildItems = childRecords?.filter(
        (v) => v?.child_vehicle === 'All' || v?.child_vehicle === '' || v?.child_vehicle?.includes(';'),
      );
      const singleChildItems = childRecords?.filter(
        (v) => v?.child_vehicle !== 'All' && v?.child_vehicle !== '' && !v?.child_vehicle?.includes(';'),
      );

      multipleChildItems?.forEach((v) => {
        // Record with all child vehicles items
        if (v?.child_vehicle === '' || v?.child_vehicle === 'All') {
          const children = mapCSVDataToOptimizationCurves({
            csvDataItems: [],
            vehicleSpreadKey: 'vehicle',
            parentVehicle,
            availabilities,
            includeChildren: false,
            enableAllChildren: true,
            defaultValuesIfCSVItemsAreEmtpy: {
              geoSpread: v?.geo_group,
            },
          });

          const { geoSpreadPayload, dateRangePayload, brandsPayload, spendPayload } = createCommonPayloads(
            v,
            availabilities,
          );

          const parentCurve: OptimizationCurve = {
            brands: null,
            vehicleName: parentVehicle,
            vehicleSpread: 'parent_vehicle',
            geoSpread: v?.geo_group || '',
            geoNames: null,
            minSpend: undefined,
            maxSpend: undefined,
            children,
            dateRange: dateRangePayload,
            spread_type: v?.spread_type ?? DEFAULT_SPREAD_TYPE,
            ...Object.fromEntries(brandsPayload),
            ...Object.fromEntries(geoSpreadPayload),
            ...Object.fromEntries(spendPayload),
          };

          curves?.push(parentCurve);
        } else {
          // Records which multiple children separate by semicolons
          const children = mapCSVDataToOptimizationCurves({
            csvDataItems: [v],
            vehicleSpreadKey: 'vehicle',
            parentVehicle,
            availabilities,
            includeChildren: false,
            isKeyHavingMultipleChildVehicles: true,
          });

          const { geoSpreadPayload, dateRangePayload, brandsPayload, spendPayload } = createCommonPayloads(
            v,
            availabilities,
          );

          const parentCurve: OptimizationCurve = {
            brands: null,
            vehicleName: parentVehicle,
            vehicleSpread: 'parent_vehicle',
            geoSpread: children?.[0]?.geoSpread ?? '',
            geoNames: null,
            minSpend: undefined,
            maxSpend: undefined,
            children,
            dateRange: dateRangePayload,
            spread_type: v?.spread_type ?? DEFAULT_SPREAD_TYPE,
            ...Object.fromEntries(brandsPayload),
            ...Object.fromEntries(geoSpreadPayload),
            ...Object.fromEntries(spendPayload),
          };
          curves?.push(parentCurve);
        }
      });

      if (singleChildItems && singleChildItems?.length > 0) {
        const children = mapCSVDataToOptimizationCurves({
          csvDataItems: singleChildItems,
          vehicleSpreadKey: 'vehicle',
          parentVehicle,
          availabilities,
          includeChildren: false,
        });

        let parentCurve: OptimizationCurve = {
          brands: null,
          vehicleName: parentVehicle,
          vehicleSpread: 'parent_vehicle',
          geoSpread: children?.[0]?.geoSpread ?? '',
          geoNames: null,
          dateRange: null,
          minSpend: undefined,
          maxSpend: undefined,
          spread_type: children?.[0]?.spread_type ?? DEFAULT_SPREAD_TYPE,
          children,
        };

        // Handle geo_spread consistency and aggregation
        if (children?.length > 0) {
          const allHaveSameGeoSpread = children.every((child) => child.geoSpread === parentCurve.geoSpread);

          if (parentCurve.geoSpread && !allHaveSameGeoSpread) {
            isWarningFlagged = true;
          }

          if (parentCurve.geoSpread && allHaveSameGeoSpread) {
            parentCurve = aggregateValuesIntoParent(parentCurve, children);
          }
        }
        curves?.push(parentCurve);
      }
    }

    return curves;
  });

  return {
    filteredRows: finalResponseItems,
    errorRows: errorRowsArray,
    validRowsCount: filteredParsedData.length,
    isWarningFlagged,
  };
};

export const addUniqueIdToCurves = (curves: OptimizationCurve[]): OptimizationCurve[] =>
  curves?.map((curve) => {
    const uniqueId = crypto.randomUUID();

    return {
      ...curve,
      id: uniqueId,
    };
  });
